import React, { useRef, useState, useEffect } from "react";
import TextInputComp from "./TextInputComp";
import CommonButton from "../CommonButton";
import { IUserProfileRequest } from "../../types/Request/CustomerRequest";
import DatePicker from "react-datepicker";
import Autocomplete from "react-google-autocomplete";
import "react-datepicker/dist/react-datepicker.css";

import {
  CalendarDays,
  Camera,
  Mail,
  Phone,
  Loader2,
  LocateFixed,
  MapPin,
} from "lucide-react";

import { MAPS_KEY } from "../../config/apiUrlsCustomer";
import { useUploadCustomerFileMutation } from "../../redux/actions/Customer/profileCustomer";

import Dropdown from "../customer/Dropdown";
import { countryCodes } from "../../utils/Customer/CustomerData";

interface ProfileUIProps {
  onSave: (
    data: IUserProfileRequest & {
      email?: string;
      phoneNumber?: string;
    },
  ) => void;

  initialData?: Partial<IUserProfileRequest> & {
    email?: string;
    phoneNumber?: string;
  };
}

// Required fields:
// firstName, lastName, email, phoneNumber
interface FieldErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
}

const ProfileUI: React.FC<ProfileUIProps> = ({ onSave, initialData }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [uploadFile, { isLoading: isUploading }] =
    useUploadCustomerFileMutation();

  // ------------------------------------------------
  // Personal Information
  // ------------------------------------------------

  const [firstName, setFirstName] = useState(initialData?.firstName || "");

  const [lastName, setLastName] = useState(initialData?.lastName || "");

  const [email, setEmail] = useState(initialData?.email || "");

  // ------------------------------------------------
  // Phone
  // Same structure as working AddressForm
  // ------------------------------------------------

  const [selectedCode, setSelectedCode] = useState(countryCodes[0]);

  const [phone, setPhone] = useState("");

  // ------------------------------------------------
  // Other Profile Information
  // ------------------------------------------------

  const [isFetchingLocation, setIsFetchingLocation] = useState(false);

  const [profileImageUrl, setProfileImageUrl] = useState(
    initialData?.profileImageUrl || "",
  );

  const [dateOfBirth, setDateOfBirth] = useState<Date | null>(
    initialData?.dateOfBirth ? new Date(initialData.dateOfBirth) : null,
  );

  const [gender, setGender] = useState(initialData?.gender || "");

  const [latitude, setLatitude] = useState(initialData?.latitude || 0);

  const [longitude, setLongitude] = useState(initialData?.longitude || 0);

  const [city, setCity] = useState(initialData?.city || "");

  const [stateName, setStateName] = useState(initialData?.state || "");

  const [country, setCountry] = useState(initialData?.country || "");

  const [postalCode, setPostalCode] = useState(initialData?.postalCode || "");

  const [address, setAddress] = useState("");

  const [preferredLanguage, setPreferredLanguage] = useState(
    initialData?.preferredLanguage || "English",
  );

  const [marketingOptIn, setMarketingOptIn] = useState(
    initialData?.marketingOptIn || false,
  );

  // ------------------------------------------------
  // Validation
  // ------------------------------------------------

  const [errors, setErrors] = useState<FieldErrors>({});

  // ------------------------------------------------
  // Load initial profile data
  // ------------------------------------------------

  useEffect(() => {
    if (!initialData) return;

    if (initialData.firstName) {
      setFirstName(initialData.firstName);
    }

    if (initialData.lastName) {
      setLastName(initialData.lastName);
    }

    if (initialData.email) {
      setEmail(initialData.email);
    }

    // ----------------------------------------------
    // Phone
    // Convert:
    // +971501234567 -> country +971 / phone 501234567
    // +919876543210 -> country +91  / phone 9876543210
    // ----------------------------------------------

    if (initialData.phoneNumber) {
      let rawPhone = initialData.phoneNumber;

      if (rawPhone.startsWith("+971")) {
        setSelectedCode(
          countryCodes.find((code) => code.value === "AE") || countryCodes[0],
        );

        rawPhone = rawPhone.replace("+971", "");
      } else if (rawPhone.startsWith("+91")) {
        setSelectedCode(
          countryCodes.find((code) => code.value === "IN") || countryCodes[1],
        );

        rawPhone = rawPhone.replace("+91", "");
      }

      setPhone(rawPhone.replace(/\D/g, ""));
    }

    if (initialData.profileImageUrl) {
      setProfileImageUrl(initialData.profileImageUrl);
    }

    if (initialData.gender) {
      setGender(initialData.gender);
    }

    if (initialData.city) {
      setCity(initialData.city);
    }

    if (initialData.state) {
      setStateName(initialData.state);
    }

    if (initialData.country) {
      setCountry(initialData.country);
    }

    if (initialData.postalCode) {
      setPostalCode(initialData.postalCode);
    }

    if (initialData.preferredLanguage) {
      setPreferredLanguage(initialData.preferredLanguage);
    }

    if (initialData.marketingOptIn !== undefined) {
      setMarketingOptIn(initialData.marketingOptIn);
    }

    if (initialData.dateOfBirth) {
      setDateOfBirth(new Date(initialData.dateOfBirth));
    }

    // If latitude / longitude already exist,
    // keep them.
    if (initialData.latitude !== undefined && initialData.latitude !== null) {
      setLatitude(Number(initialData.latitude));
    }

    if (initialData.longitude !== undefined && initialData.longitude !== null) {
      setLongitude(Number(initialData.longitude));
    }
  }, [initialData]);

  // ------------------------------------------------
  // Validation
  // ------------------------------------------------

  const validateRequiredFields = (): boolean => {
    const newErrors: FieldErrors = {};

    // First name
    if (!firstName.trim()) {
      newErrors.firstName = "First name is required.";
    }

    // Last name
    if (!lastName.trim()) {
      newErrors.lastName = "Last name is required.";
    }

    // Email
    if (!email.trim()) {
      newErrors.email = "Email address is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      newErrors.email = "Enter a valid email address.";
    }

    // ------------------------------------------------
    // Phone validation
    // UAE:
    // 9 digits
    // Must start with 5
    // Example: 501234567
    // ------------------------------------------------

    if (!phone.trim()) {
      newErrors.phoneNumber = "Phone number is required.";
    } else if (selectedCode.value === "AE") {
      if (!/^5\d{8}$/.test(phone)) {
        newErrors.phoneNumber =
          "Enter a valid UAE mobile number starting with 5.";
      }
    } else {
      // Other configured countries
      const expectedPhoneLength = 10;

      if (phone.length !== expectedPhoneLength) {
        newErrors.phoneNumber = `Enter a valid ${expectedPhoneLength}-digit phone number.`;
      }
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const clearError = (field: keyof FieldErrors) => {
    if (errors[field]) {
      setErrors((previous) => ({
        ...previous,
        [field]: undefined,
      }));
    }
  };

  // ------------------------------------------------
  // Profile Image Upload
  // ------------------------------------------------

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (!file) return;

    try {
      const response = await uploadFile({
        File: file,
        FolderName: "Customers/Avatars",
      }).unwrap();

      setProfileImageUrl(response.fileUrl);
    } catch (error) {
      console.error("Profile image upload failed:", error);

      alert("Profile image upload failed. Please try again.");
    }
  };

  // ------------------------------------------------
  // Update Profile
  // ------------------------------------------------

  const handleUpdateProfile = () => {
    if (isUploading) return;

    if (!validateRequiredFields()) {
      return;
    }

    // ----------------------------------------------
    // Build complete international phone number
    //
    // Example:
    // +971 + 501234567
    // = +971501234567
    // ----------------------------------------------

    const completePhoneNumber = `${selectedCode.label}${phone.trim()}`;

    const profilePayload: IUserProfileRequest & {
      email?: string;
      phoneNumber?: string;
    } = {
      firstName,
      lastName,
      email,
      phoneNumber: completePhoneNumber,

      profileImageUrl,

      dateOfBirth: dateOfBirth ? dateOfBirth.toISOString() : null,

      gender,

      latitude,
      longitude,

      city,
      state: stateName,
      country,
      postalCode,

      preferredLanguage,
      marketingOptIn,
    };

    onSave(profilePayload);
  };

  // ------------------------------------------------
  // Reverse Geocode Current Location
  // ------------------------------------------------

  const handleCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported.");

      return;
    }

    setIsFetchingLocation(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;

        const lng = position.coords.longitude;

        setLatitude(lat);
        setLongitude(lng);

        try {
          const response = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${MAPS_KEY}`,
          );

          const data = await response.json();

          if (data.status === "OK" && data.results?.length) {
            const result = data.results[0];

            setAddress(result.formatted_address || "");

            const components = result.address_components || [];

            const getComponent = (type: string) =>
              components.find((component: any) =>
                component.types.includes(type),
              )?.long_name || "";

            setCity(
              getComponent("locality") ||
                getComponent("sublocality_level_1") ||
                "",
            );

            setStateName(getComponent("administrative_area_level_1") || "");

            setCountry(getComponent("country") || "");

            setPostalCode(getComponent("postal_code") || "");
          }
        } catch (error) {
          console.error("Reverse geocoding failed:", error);

          alert("Unable to determine your address.");
        } finally {
          setIsFetchingLocation(false);
        }
      },
      () => {
        setIsFetchingLocation(false);

        alert("Unable to get your location.");
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
      },
    );
  };

  // ------------------------------------------------
  // Shared Input Styles
  // ------------------------------------------------

  const inputBase =
    "w-full h-[54px] rounded-2xl border bg-[#FAFBF8] pl-12 pr-4 text-[#2F5E2E] text-sm shadow-sm outline-none transition-all duration-200 disabled:opacity-60";

  const inputNormal = `${inputBase} border-[#DDE7D8] focus:border-[#6DBE45]`;

  const inputError = `${inputBase} border-red-400 focus:border-red-400`;

  // ------------------------------------------------
  // UI
  // ------------------------------------------------

  return (
    <div className="max-w-5xl mx-auto p-6 min-h-screen relative">
      {/* -------------------------------------------- */}
      {/* Upload Overlay */}
      {/* -------------------------------------------- */}

      {isUploading && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-xs z-50 flex items-center justify-center rounded-2xl">
          <div className="bg-white rounded-2xl p-6 flex items-center gap-3 shadow-xl">
            <Loader2 className="w-5 h-5 animate-spin text-[#6DBE45]" />

            <span className="font-semibold text-sm text-slate-700">
              Uploading avatar...
            </span>
          </div>
        </div>
      )}

      {/* -------------------------------------------- */}
      {/* Header */}
      {/* -------------------------------------------- */}

      <div className="mb-8">
        <h2 className="text-3xl font-bold text-[#2F5E2E]">My Profile</h2>

        <p className="text-gray-500 mt-1">
          Manage your personal information, contact details, and preferences.
        </p>
      </div>

      {/* -------------------------------------------- */}
      {/* Profile Card */}
      {/* -------------------------------------------- */}

      <div className="bg-white rounded-3xl shadow-sm border border-[#A8D97B]/20 overflow-hidden">
        {/* Banner */}
        <div className="h-32 bg-gradient-to-r from-[#A8D97B] to-[#6DBE45]" />

        <div className="px-8 pb-8">
          {/* ---------------------------------------- */}
          {/* Avatar */}
          {/* ---------------------------------------- */}

          <div className="-mt-16 flex items-end justify-between">
            <div className="flex items-end gap-5">
              <div className="relative">
                <img
                  src={
                    profileImageUrl ||
                    "https://cdn-icons-png.flaticon.com/512/847/847969.png"
                  }
                  alt="profile"
                  className="w-32 h-32 rounded-full border-4 border-white object-cover shadow-lg"
                />

                <button
                  type="button"
                  disabled={isUploading}
                  onClick={() => fileInputRef.current?.click()}
                  className="absolute bottom-2 right-2 bg-[#2F5E2E] p-2 rounded-full text-white hover:scale-105 transition disabled:opacity-50 disabled:scale-100"
                >
                  <Camera size={18} />
                </button>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  disabled={isUploading}
                  onChange={handleImageUpload}
                />
              </div>

              <div className="pb-3">
                <h3 className="text-2xl font-bold text-[#2F5E2E]">
                  {firstName || "Your"} {lastName || "Profile"}
                </h3>

                <p className="text-gray-500">
                  Update your personal and contact details
                </p>
              </div>
            </div>
          </div>

          {/* ---------------------------------------- */}
          {/* Personal Information */}
          {/* ---------------------------------------- */}

          <div className="mt-10">
            <div className="flex items-baseline gap-3 mb-5">
              <h3 className="text-xl font-semibold text-[#2F5E2E]">
                Personal Information
              </h3>

              <span className="text-xs text-gray-400">
                Fields marked <span className="text-red-400">*</span> are
                required
              </span>
            </div>

            <div className="grid md:grid-cols-2 gap-5">
              {/* ---------------------------------- */}
              {/* First Name */}
              {/* ---------------------------------- */}

              <div className="flex flex-col">
                <label className="text-sm font-medium mb-2 block">
                  First Name <span className="text-red-400">*</span>
                </label>

                <TextInputComp
                  disabled={isUploading}
                  value={firstName}
                  onChangeText={(value) => {
                    setFirstName(value);
                    clearError("firstName");
                  }}
                  placeholder="First Name"
                  label="First Name"
                  showLabel={false}
                  className={`py-3 ${errors.firstName ? "border-red-400" : ""}`}
                />

                {errors.firstName && (
                  <p className="text-red-400 text-xs mt-1">
                    {errors.firstName}
                  </p>
                )}
              </div>

              {/* ---------------------------------- */}
              {/* Last Name */}
              {/* ---------------------------------- */}

              <div className="flex flex-col">
                <label className="text-sm font-medium mb-2 block">
                  Last Name <span className="text-red-400">*</span>
                </label>

                <TextInputComp
                  disabled={isUploading}
                  value={lastName}
                  onChangeText={(value) => {
                    setLastName(value);
                    clearError("lastName");
                  }}
                  placeholder="Last Name"
                  label="Last Name"
                  showLabel={false}
                  className={`py-3 ${errors.lastName ? "border-red-400" : ""}`}
                />

                {errors.lastName && (
                  <p className="text-red-400 text-xs mt-1">{errors.lastName}</p>
                )}
              </div>

              {/* ---------------------------------- */}
              {/* Email */}
              {/* ---------------------------------- */}

              <div className="flex flex-col">
                <label className="text-sm font-medium mb-2 block">
                  Email Address <span className="text-red-400">*</span>
                </label>

                <div className="relative flex items-center">
                  <Mail size={18} className="absolute left-4 text-[#6DBE45]" />

                  <input
                    type="email"
                    disabled={isUploading}
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      clearError("email");
                    }}
                    placeholder="example@domain.com"
                    className={errors.email ? inputError : inputNormal}
                  />
                </div>

                {errors.email && (
                  <p className="text-red-400 text-xs mt-1">{errors.email}</p>
                )}
              </div>

              {/* ---------------------------------- */}
              {/* Phone Number */}
              {/* ---------------------------------- */}

              <div className="flex flex-col">
                <label className="text-sm font-medium mb-2 block">
                  Phone Number <span className="text-red-400">*</span>
                </label>

                <div className="flex items-start gap-2 w-full">
                  {/* Country Code */}
                  <div className="w-[100px] flex-shrink-0">
                    <Dropdown
                      options={countryCodes}
                      value={selectedCode}
                      onChange={(code) => {
                        setSelectedCode(code);

                        // Clear phone when country changes
                        setPhone("");

                        clearError("phoneNumber");
                      }}
                      iconPosition="right"
                      className="w-full"
                    />
                  </div>

                  {/* Phone Number */}
                  <div className="flex-1 min-w-0">
                    <div className="relative flex items-center">
                      <Phone
                        size={18}
                        className="absolute left-4 text-[#6DBE45] z-10"
                      />

                      <input
                        type="tel"
                        disabled={isUploading}
                        value={phone}
                        onChange={(e) => {
                          const numericText = e.target.value.replace(/\D/g, "");

                          setPhone(numericText);
                          clearError("phoneNumber");
                        }}
                        maxLength={selectedCode.value === "AE" ? 9 : 10}
                        placeholder="Enter mobile number"
                        className={
                          errors.phoneNumber ? inputError : inputNormal
                        }
                      />
                    </div>

                    {errors.phoneNumber && (
                      <p className="text-red-400 text-xs mt-1">
                        {errors.phoneNumber}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* ---------------------------------- */}
              {/* Date Of Birth */}
              {/* ---------------------------------- */}

              <div>
                <label className="text-sm font-medium mb-2 flex items-center gap-2">
                  Date of Birth
                </label>

                <div className="relative">
                  <CalendarDays
                    size={18}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6DBE45] z-10"
                  />

                  <DatePicker
                    selected={dateOfBirth}
                    onChange={(date: Date | null) => setDateOfBirth(date)}
                    dateFormat="dd MMM yyyy"
                    placeholderText="Select your date of birth"
                    maxDate={new Date()}
                    showYearDropdown
                    scrollableYearDropdown
                    yearDropdownItemNumber={100}
                    dropdownMode="select"
                    popperPlacement="bottom-start"
                    calendarClassName="custom-datepicker"
                    popperClassName="z-[9999]"
                    wrapperClassName="w-full"
                    disabled={isUploading}
                    onKeyDown={(e) => e.preventDefault()}
                    isClearable
                    customInput={
                      <input
                        readOnly
                        className="w-full h-[54px] rounded-2xl border border-[#DDE7D8] bg-[#FAFBF8] pl-12 pr-4 text-[#2F5E2E] text-sm shadow-sm outline-none transition-all duration-200 focus:border-[#6DBE45] disabled:opacity-60"
                      />
                    }
                  />
                </div>
              </div>

              {/* ---------------------------------- */}
              {/* Gender */}
              {/* ---------------------------------- */}

              <div>
                <label className="text-sm font-medium mb-2 flex items-center gap-2">
                  Gender
                </label>

                <select
                  disabled={isUploading}
                  value={gender}
                  onChange={(e) => setGender(e.target.value)}
                  className="w-full h-[54px] border border-[#DDE7D8] bg-[#FAFBF8] rounded-2xl px-4 text-sm text-[#2F5E2E] outline-none transition-all duration-200 focus:border-[#6DBE45] shadow-sm appearance-none disabled:opacity-60"
                  style={{
                    backgroundImage:
                      "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%232F5E2E' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'></polyline></svg>\")",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "right 16px center",
                    backgroundSize: "16px",
                  }}
                >
                  <option value="">Prefer not to say</option>

                  <option value="male">Male</option>

                  <option value="female">Female</option>

                  <option value="other">Other</option>
                </select>
              </div>
            </div>
          </div>

          {/* ---------------------------------------- */}
          {/* Address Information */}
          {/* ---------------------------------------- */}

          <div className="mt-10">
            <div className="flex items-baseline gap-3 mb-5">
              <h3 className="text-xl font-semibold text-[#2F5E2E]">
                Address Information
              </h3>
            </div>

            {/* Google Address Lookup */}

            <div className="mb-5">
              <label className="text-sm font-medium mb-2 block">
                Address Lookup
              </label>

              <div className="relative">
                {/* Current Location */}
                <button
                  type="button"
                  onClick={handleCurrentLocation}
                  disabled={isUploading || isFetchingLocation}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#2F5E2E] hover:text-[#6DBE45] z-20 disabled:opacity-50"
                  title="Use my current location"
                >
                  {isFetchingLocation ? (
                    <Loader2 size={18} className="animate-spin" />
                  ) : (
                    <LocateFixed size={18} />
                  )}
                </button>

                <MapPin
                  size={18}
                  className="absolute left-4 top-[18px] text-[#6DBE45] z-10"
                />

                {/* Google Autocomplete */}
                <Autocomplete
                  apiKey={MAPS_KEY}
                  options={{
                    fields: [
                      "formatted_address",
                      "geometry",
                      "address_components",
                    ],

                    componentRestrictions: {
                      country: "ae",
                    },

                    types: ["geocode", "establishment"],
                  }}
                  className="w-full h-[54px] border border-[#DDE7D8] bg-[#FAFBF8] rounded-2xl py-3 pl-12 pr-12 text-sm outline-none focus:border-[#6DBE45] transition-all duration-200"
                  defaultValue={address}
                  placeholder="Search address..."
                  onPlaceSelected={(place: any) => {
                    if (!place?.address_components) {
                      return;
                    }

                    const components = place.address_components;

                    const getComponent = (type: string) =>
                      components.find((component: any) =>
                        component.types.includes(type),
                      )?.long_name || "";

                    // --------------------------------
                    // Address
                    // --------------------------------

                    setAddress(place.formatted_address || "");

                    // --------------------------------
                    // City
                    // --------------------------------

                    setCity(
                      getComponent("locality") ||
                        getComponent("sublocality_level_1") ||
                        "",
                    );

                    // --------------------------------
                    // State
                    // --------------------------------

                    setStateName(
                      getComponent("administrative_area_level_1") || "",
                    );

                    // --------------------------------
                    // Country
                    // --------------------------------

                    setCountry(getComponent("country") || "");

                    // --------------------------------
                    // Postal Code
                    // --------------------------------

                    setPostalCode(getComponent("postal_code") || "");

                    // --------------------------------
                    // Latitude / Longitude
                    // --------------------------------

                    if (place.geometry?.location) {
                      const lat =
                        typeof place.geometry.location.lat === "function"
                          ? place.geometry.location.lat()
                          : place.geometry.location.lat;

                      const lng =
                        typeof place.geometry.location.lng === "function"
                          ? place.geometry.location.lng()
                          : place.geometry.location.lng;

                      setLatitude(lat);
                      setLongitude(lng);
                    }
                  }}
                />
              </div>
            </div>

            {/* -------------------------------------- */}
            {/* Address Details */}
            {/* -------------------------------------- */}

            <div className="grid md:grid-cols-2 gap-5">
              <TextInputComp
                disabled={isUploading}
                value={city}
                onChangeText={setCity}
                placeholder="City"
                label="City"
                showLabel
              />

              <TextInputComp
                disabled={isUploading}
                value={stateName}
                onChangeText={setStateName}
                placeholder="State"
                label="State"
                showLabel
              />

              <TextInputComp
                disabled={isUploading}
                value={country}
                onChangeText={setCountry}
                placeholder="Country"
                label="Country"
                showLabel
              />

              <TextInputComp
                disabled={isUploading}
                value={postalCode}
                onChangeText={setPostalCode}
                placeholder="Postal Code"
                label="Postal Code"
                showLabel
              />
            </div>
          </div>

          {/* ---------------------------------------- */}
          {/* Preferences */}
          {/* ---------------------------------------- */}

          <div className="mt-10">
            <div className="flex items-baseline gap-3 mb-5">
              <h3 className="text-xl font-semibold text-[#2F5E2E]">
                Preferences
              </h3>
            </div>

            <div className="grid md:grid-cols-2 gap-5">
              {/* Preferred Language */}

              <div>
                <label className="text-sm font-medium mb-2 block">
                  Preferred Language
                </label>

                <select
                  disabled={isUploading}
                  value={preferredLanguage}
                  onChange={(e) => setPreferredLanguage(e.target.value)}
                  className="w-full h-[54px] border border-[#DDE7D8] bg-[#FAFBF8] rounded-2xl px-4 text-sm text-[#2F5E2E] outline-none transition-all duration-200 focus:border-[#6DBE45] shadow-sm appearance-none disabled:opacity-60"
                  style={{
                    backgroundImage:
                      "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%232F5E2E' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'></polyline></svg>\")",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "right 16px center",
                    backgroundSize: "16px",
                  }}
                >
                  <option>English</option>

                  <option>Arabic</option>
                </select>
              </div>

              {/* Marketing */}

              <div className="flex items-center pt-9">
                <input
                  type="checkbox"
                  id="marketingOptInCheckbox"
                  disabled={isUploading}
                  checked={marketingOptIn}
                  onChange={(e) => setMarketingOptIn(e.target.checked)}
                  className="w-5 h-5 rounded accent-[#2F5E2E] disabled:opacity-50"
                />

                <label
                  htmlFor="marketingOptInCheckbox"
                  className="ml-3 text-sm text-gray-600 select-none cursor-pointer"
                >
                  Receive marketing updates & email notices
                </label>
              </div>
            </div>
          </div>

          {/* ---------------------------------------- */}
          {/* Submit */}
          {/* ---------------------------------------- */}

          <div className="flex justify-end mt-10 border-t pt-6">
            <CommonButton
              text="Update Profile"
              variant="filled"
              rounded="md"
              disabled={isUploading}
              className="px-8 py-3 font-semibold shadow-xs"
              onClick={handleUpdateProfile}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileUI;
